import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { FaArrowLeft } from 'react-icons/fa';
import Loader from '../../Loader';
import {
  getAdminAccountDetail,
  getAdminPlans,
  provisionApiProduction,
  setApiQuota,
  setReaderPlan,
  cancelReaderPlan,
  provisionReaderEnterprise,
  cancelApiPlan,
  downgradeApiToTrial,
  extendReaderTrial,
  getAccountAudit,
  getUserByClientId,
} from '../../../services/axios';

const mapStateToProps = (state) => ({
  token: state.user.user ? state.user.user.token : null,
});

const connector = connect(mapStateToProps);

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());

// Billing-period suffix for a recorded negotiated price ("Monthly"→/mo, "Annual"→/yr, else none).
const intervalSuffix = (iv) => (iv === 'Annual' ? '/yr' : iv === 'Monthly' ? '/mo' : '');
const priceTag = (price, iv) =>
  `$${Number(price).toLocaleString()}${intervalSuffix(iv)} negotiated`;

const shortDate = (d) =>
  d
    ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

const daysUntil = (d) => Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);

// A negotiated price expressed per year, so the two product lines can be summed.
const annualised = (price, iv) => (price == null ? 0 : iv === 'Annual' ? Number(price) : Number(price) * 12);

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'app', label: 'App subscription' },
  { key: 'api', label: 'API subscription' },
  { key: 'team', label: 'Team' },
  { key: 'activity', label: 'Activity' },
];

// Admin: one customer's page — its two product subscriptions, commercial terms, team and
// activity, on tabs. Every control here was previously stacked in a single detail panel; the
// endpoints behind them are unchanged.
const CustomerDetail = ({ token, clientId, onBack }) => {
  const [tab, setTab] = useState('overview');

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [audit, setAudit] = useState([]); // account activity history
  const [members, setMembers] = useState(null); // org logins (null = not loaded, {error} = refused)

  const [busy, setBusy] = useState(false);
  const [limitInput, setLimitInput] = useState('');
  const [apiPrice, setApiPrice] = useState(''); // negotiated API Enterprise price input
  const [apiInterval, setApiInterval] = useState('Monthly'); // billing period for that price
  const [apiSeats, setApiSeats] = useState(''); // user seats for the API Enterprise deal
  const [apiAdminEmail, setApiAdminEmail] = useState(''); // customer's CorporateAdmin
  const [apiAdminFirst, setApiAdminFirst] = useState('');
  const [apiAdminLast, setApiAdminLast] = useState('');
  const [quotaInterval, setQuotaInterval] = useState('Monthly'); // period the call limit meters over
  const [msg, setMsg] = useState(null);

  const [readerPlans, setReaderPlans] = useState([]);
  const [readerPlanSel, setReaderPlanSel] = useState('');
  const [readerBusy, setReaderBusy] = useState(false);
  const [readerMsg, setReaderMsg] = useState(null);
  const [readerPrice, setReaderPrice] = useState(''); // negotiated Enterprise price input
  const [readerInterval, setReaderInterval] = useState('Monthly'); // billing period for that price
  const [readerSeats, setReaderSeats] = useState(''); // user seats for the reader Enterprise deal
  const [readerAdminEmail, setReaderAdminEmail] = useState(''); // customer's CorporateAdmin
  const [readerAdminFirst, setReaderAdminFirst] = useState('');
  const [readerAdminLast, setReaderAdminLast] = useState('');
  const [extendDays, setExtendDays] = useState('14'); // days to add to an App trial

  // Contract terms — per-product deal (dates + PO + notes) and org-level email domains.
  const [apiStart, setApiStart] = useState('');
  const [apiEnd, setApiEnd] = useState('');
  const [apiPO, setApiPO] = useState('');
  const [apiNotes, setApiNotes] = useState('');
  const [readerStart, setReaderStart] = useState('');
  const [readerEnd, setReaderEnd] = useState('');
  const [readerPO, setReaderPO] = useState('');
  const [readerNotes, setReaderNotes] = useState('');
  const [allowedDomains, setAllowedDomains] = useState(''); // org-level

  // ISO datetime → yyyy-mm-dd for <input type="date"> (empty when absent).
  const toDateInput = (iso) => (iso ? String(iso).slice(0, 10) : '');

  const load = useCallback(
    async (id) => {
      if (!token) return;
      setDetailLoading(true);
      setMsg(null);
      const res = await getAdminAccountDetail(token, id);
      setDetail(res);
      getAccountAudit(token, id).then(setAudit);
      setLimitInput(res && res.api && res.api.callLimit ? String(res.api.callLimit) : '');
      setApiPrice(res && res.api && res.api.priceOverride != null ? String(res.api.priceOverride) : '');
      setApiInterval((res && res.api && res.api.priceInterval) || 'Monthly');
      setApiSeats(res && res.api && res.api.seats != null ? String(res.api.seats) : '');
      // What the call limit meters over. An override with no interval IS a lifetime total, so the
      // absence of one is meaningful rather than missing. Default a trial to a one-off top-up: a
      // period would convert its fixed allowance into one that renews forever.
      const apiSum = (res && res.api) || {};
      const apiOnTrial =
        !!apiSum.planName && !apiSum.planName.toLowerCase().includes('production');
      setQuotaInterval(
        apiSum.quotaInterval || (apiSum.quotaOverride != null || apiOnTrial ? 'Total' : 'Monthly')
      );
      setReaderPlanSel(res && res.reader && res.reader.planId ? String(res.reader.planId) : '');
      setReaderPrice(
        res && res.reader && res.reader.priceOverride != null ? String(res.reader.priceOverride) : ''
      );
      setReaderInterval((res && res.reader && res.reader.priceInterval) || 'Monthly');
      setReaderSeats(res && res.reader && res.reader.seats != null ? String(res.reader.seats) : '');
      // Admin defaults to the org's existing account holder (the common "same person" case);
      // the admin can override to name a different CorporateAdmin.
      setApiAdminEmail((res && res.email) || '');
      setReaderAdminEmail((res && res.email) || '');
      setApiAdminFirst('');
      setApiAdminLast('');
      setReaderAdminFirst('');
      setReaderAdminLast('');
      setApiStart(toDateInput(res && res.api && res.api.startDate));
      setApiEnd(toDateInput(res && res.api && res.api.endDate));
      setApiPO((res && res.api && res.api.purchaseOrder) || '');
      setApiNotes((res && res.api && res.api.notes) || '');
      setReaderStart(toDateInput(res && res.reader && res.reader.startDate));
      setReaderEnd(toDateInput(res && res.reader && res.reader.endDate));
      setReaderPO((res && res.reader && res.reader.purchaseOrder) || '');
      setReaderNotes((res && res.reader && res.reader.notes) || '');
      setAllowedDomains((res && res.allowedDomains) || '');
      setReaderMsg(null);
      setDetailLoading(false);
    },
    [token]
  );

  useEffect(() => {
    load(clientId);
  }, [clientId, load]);

  // Reader plans for the reader-control dropdown (loaded once).
  useEffect(() => {
    if (!token) return;
    (async () => {
      const plans = await getAdminPlans(token);
      setReaderPlans((plans || []).filter((p) => p.productLine === 'ReaderApp'));
    })();
  }, [token]);

  // Organisation logins — only fetched when the Team tab is opened.
  useEffect(() => {
    if (tab !== 'team' || !token || members !== null) return;
    (async () => {
      const res = await getUserByClientId(clientId, token);
      setMembers(Array.isArray(res) ? res : { error: (res && res.reason) || 'Could not load members.' });
    })();
  }, [tab, token, clientId, members]);

  const refreshDetail = () => detail && load(detail.clientId);

  const api = (detail && detail.api) || {};
  const reader = (detail && detail.reader) || {};
  const isProduction = api.planName && api.planName.toLowerCase().includes('production');
  const isReaderEnterprise = reader.planName && reader.planName.toLowerCase().includes('enterprise');
  // The two products define "trial" differently: the API's is capped by a call allowance, the App's
  // by a clock — so they are extended in different ways (top up the allowance vs add days).
  const isApiTrial = api.hasApiSubscription && !isProduction;
  const isReaderTrial = reader.hasSubscription && reader.status === 'Trialing';
  const hasQuotaPeriod = !!api.quotaInterval;
  const isAnnualQuota = api.quotaInterval === 'Annual';
  const quotaPeriodLong = hasQuotaPeriod ? (isAnnualQuota ? 'this year' : 'this month') : 'total';
  const quotaPeriodShort = hasQuotaPeriod ? (isAnnualQuota ? '/yr' : '/mo') : '';
  // Admin display wording matches the customer-facing + reader model: Trial / Enterprise
  // (the backend plan is still "API Production"/"API Trial" internally).
  const apiPlanLabel = api.planName
    ? isProduction
      ? 'Enterprise'
      : api.planName.replace(/^API\s+/i, '')
    : '';

  // Parse a seats input → positive int or null (null = leave the seat cap untouched).
  const parseSeats = (v) => {
    const n = parseInt(v, 10);
    return !isNaN(n) && n > 0 ? n : null;
  };

  const handleProvision = async () => {
    if (!detail || !token) return;
    const p = parseFloat(apiPrice);
    const price = !isNaN(p) && p >= 0 ? p : null;
    setBusy(true);
    setMsg(null);
    const res = await provisionApiProduction(token, detail.clientId, price, apiInterval, {
      seats: parseSeats(apiSeats),
      adminEmail: apiAdminEmail.trim(),
      adminFirstName: apiAdminFirst.trim(),
      adminLastName: apiAdminLast.trim(),
      startDate: apiStart || null,
      endDate: apiEnd || null,
      purchaseOrder: apiPO,
      notes: apiNotes,
      allowedDomains: allowedDomains,
    });
    setBusy(false);
    if (res && res.success) {
      setMsg('✓ ' + (res.reason || 'Provisioned on the Enterprise plan.'));
      refreshDetail();
    } else setMsg('Could not provision Enterprise. ' + ((res && res.reason) || ''));
  };

  const handleDowngradeApi = async () => {
    if (!detail || !token) return;
    if (
      !window.confirm(
        'Downgrade this account to the free API Trial tier? Enterprise price, seats and call limit will be cleared.'
      )
    )
      return;
    setBusy(true);
    setMsg(null);
    const res = await downgradeApiToTrial(token, detail.clientId);
    setBusy(false);
    if (res && res.success) {
      setMsg('✓ ' + (res.reason || 'Downgraded to the Trial tier.'));
      refreshDetail();
    } else setMsg('Could not downgrade. ' + ((res && res.reason) || ''));
  };

  const handleCancelApi = async () => {
    if (!detail || !token) return;
    if (
      !window.confirm(
        'Cancel this API subscription and revoke the customer’s API keys? They will lose API access.'
      )
    )
      return;
    setBusy(true);
    setMsg(null);
    const res = await cancelApiPlan(token, detail.clientId);
    setBusy(false);
    if (res && res.success) {
      setMsg('✓ ' + (res.reason || 'API subscription cancelled.'));
      refreshDetail();
    } else setMsg('Could not cancel. ' + ((res && res.reason) || ''));
  };

  const handleSaveLimit = async () => {
    if (!detail || !token) return;
    const n = parseInt(limitInput, 10);
    if (isNaN(n) || n <= 0) return;
    setBusy(true);
    setMsg(null);
    const res = await setApiQuota(token, detail.clientId, n, quotaInterval);
    setBusy(false);
    if (res && res.success) {
      setMsg('✓ ' + (res.reason || 'Limit updated.'));
      refreshDetail();
    } else setMsg('Could not set limit. ' + ((res && res.reason) || ''));
  };

  const handleResetLimit = async () => {
    if (!detail || !token) return;
    setBusy(true);
    setMsg(null);
    const res = await setApiQuota(token, detail.clientId, 0);
    setBusy(false);
    if (res && res.success) {
      setMsg('✓ ' + (res.reason || 'Reverted to plan default.'));
      refreshDetail();
    } else setMsg('Could not reset limit. ' + ((res && res.reason) || ''));
  };

  const applyReaderPlan = async () => {
    if (!detail || !token) return;
    const planId = parseInt(readerPlanSel, 10);
    if (isNaN(planId)) return;
    setReaderBusy(true);
    setReaderMsg(null);
    const res = await setReaderPlan(token, detail.clientId, planId);
    setReaderBusy(false);
    if (res && res.success && res.detail) {
      setDetail(res.detail);
      setReaderMsg('✓ RegulationCity App plan updated.');
    } else setReaderMsg('Could not update the App plan. ' + ((res && res.reason) || ''));
  };

  const handleCancelReader = async () => {
    if (!detail || !token) return;
    setReaderBusy(true);
    setReaderMsg(null);
    const res = await cancelReaderPlan(token, detail.clientId);
    setReaderBusy(false);
    if (res && res.success && res.detail) {
      setDetail(res.detail);
      setReaderPlanSel('');
      setReaderMsg('✓ RegulationCity App subscription cancelled.');
    } else setReaderMsg('Could not cancel. ' + ((res && res.reason) || ''));
  };

  const handleExtendReaderTrial = async () => {
    if (!detail || !token) return;
    const days = parseInt(extendDays, 10);
    if (isNaN(days) || days <= 0) {
      setReaderMsg('Enter how many days to extend the trial by.');
      return;
    }
    setReaderBusy(true);
    setReaderMsg(null);
    const res = await extendReaderTrial(token, detail.clientId, days);
    setReaderBusy(false);
    if (res && res.success && res.detail) {
      setDetail(res.detail);
      setReaderMsg(
        `✓ Trial extended by ${days} ${days === 1 ? 'day' : 'days'} — now ends ${
          res.detail.reader && res.detail.reader.trialEndsAt
            ? new Date(res.detail.reader.trialEndsAt).toLocaleDateString()
            : 'later'
        }.`
      );
    } else setReaderMsg('Could not extend the trial. ' + ((res && res.reason) || ''));
  };

  const handleProvisionReaderEnterprise = async () => {
    if (!detail || !token) return;
    const p = parseFloat(readerPrice);
    const price = !isNaN(p) && p >= 0 ? p : null;
    setReaderBusy(true);
    setReaderMsg(null);
    const res = await provisionReaderEnterprise(token, detail.clientId, price, readerInterval, {
      seats: parseSeats(readerSeats),
      adminEmail: readerAdminEmail.trim(),
      adminFirstName: readerAdminFirst.trim(),
      adminLastName: readerAdminLast.trim(),
      startDate: readerStart || null,
      endDate: readerEnd || null,
      purchaseOrder: readerPO,
      notes: readerNotes,
      allowedDomains: allowedDomains,
    });
    setReaderBusy(false);
    if (res && res.success && res.detail) {
      setDetail(res.detail);
      setReaderPlanSel(
        res.detail.reader && res.detail.reader.planId ? String(res.detail.reader.planId) : ''
      );
      setReaderMsg('✓ ' + (res.reason || 'Provisioned on the Enterprise plan.'));
    } else setReaderMsg('Could not provision Enterprise. ' + ((res && res.reason) || ''));
  };

  if (detailLoading || !detail) {
    return (
      <div className='adm-panel'>
        <Loader />
      </div>
    );
  }

  if (detail.found === false) {
    return (
      <section className='adm-section'>
        <button className='adm-back' type='button' onClick={onBack}>
          <FaArrowLeft /> Back to customers
        </button>
        <div className='adm-empty'>No customer found for client #{clientId}.</div>
      </section>
    );
  }

  const usagePct =
    api.callLimit > 0 ? Math.min(100, Math.round((api.used / api.callLimit) * 100)) : 0;

  // Header identity: a customer is a Client, and plenty have no company name — lead with
  // whatever names them rather than showing a blank.
  const orgName = detail.company || detail.name || detail.email || `Client #${detail.clientId}`;
  const hasCompany = !!detail.company;
  const tierLabel = isReaderEnterprise || isProduction ? 'Enterprise' : 'Trial';
  const bothProducts = reader.hasSubscription && api.hasApiSubscription;
  const productWord = bothProducts
    ? 'both products'
    : api.hasApiSubscription
    ? 'Developer API'
    : reader.hasSubscription
    ? 'RegulationCity App'
    : 'no subscription';

  const contractValue = annualised(reader.priceOverride, reader.priceInterval) +
    annualised(api.priceOverride, api.priceInterval);

  // Soonest dated end across the two lines = the customer's next renewal.
  const renewalCandidates = [
    reader.hasSubscription && reader.endDate ? { date: reader.endDate, trial: reader.status === 'Trialing' } : null,
    api.hasApiSubscription && api.endDate ? { date: api.endDate, trial: api.status === 'Trialing' } : null,
  ].filter(Boolean);
  renewalCandidates.sort((a, b) => new Date(a.date) - new Date(b.date));
  const nextRenewal = renewalCandidates[0] || null;
  const renewalDays = nextRenewal ? daysUntil(nextRenewal.date) : null;

  const domainList = (allowedDomains || '')
    .split(',')
    .map((d) => d.trim())
    .filter(Boolean);

  const accessTone = (sub, granting) =>
    !sub ? '' : granting ? 'adm-status--handled' : 'adm-status--archived';

  return (
    <section className='adm-section'>
      <button className='adm-back' type='button' onClick={onBack}>
        <FaArrowLeft /> Back to customers
      </button>

      <div className='adm-orghead'>
        <div className='adm-orghead__main'>
          <div className='adm-orghead__title'>
            <h2 className='adm-orghead__name'>{orgName}</h2>
            <span className='adm-badge adm-badge--tier'>
              {tierLabel} · {productWord}
            </span>
          </div>
          <div className='adm-orgfacts'>
            <div className='adm-orgfact'>
              <div className='adm-field__label'>Primary contact</div>
              <div className='adm-orgfact__v'>
                {detail.name || '—'}
                {detail.email ? <span className='adm-orgfact__sub'> · {detail.email}</span> : null}
              </div>
            </div>
            {!hasCompany && (
              <div className='adm-orgfact'>
                <div className='adm-field__label'>Company</div>
                <div className='adm-orgfact__v adm-table__muted'>Not recorded</div>
              </div>
            )}
            <div className='adm-orgfact'>
              <div className='adm-field__label'>Seats</div>
              <div className='adm-orgfact__v'>
                {detail.seatCap ? `${detail.seatsUsed} of ${detail.seatCap}` : `${detail.seatsUsed} · no cap`}
              </div>
            </div>
            <div className='adm-orgfact'>
              <div className='adm-field__label'>Client</div>
              <div className='adm-orgfact__v'>#{detail.clientId}</div>
            </div>
          </div>
        </div>
      </div>

      <div className='adm-tabs adm-tabs--org' role='tablist'>
        {TABS.map((t) => (
          <button
            key={t.key}
            type='button'
            role='tab'
            aria-selected={tab === t.key}
            className={`adm-tab${tab === t.key ? ' is-active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {t.key === 'activity' && audit && audit.length ? (
              <span className='adm-tab__count'>{audit.length}</span>
            ) : null}
            {t.key === 'team' && Array.isArray(members) ? (
              <span className='adm-tab__count'>{members.length}</span>
            ) : null}
          </button>
        ))}
      </div>

      {/* ── Overview ── */}
      {tab === 'overview' && (
        <div className='adm-orggrid'>
          <div className='adm-panel adm-panel--flush'>
            <div className='adm-panel__head'>
              <b>Products</b>
            </div>
            <div className='adm-panel__body'>
              <div className='adm-prodrow'>
                <span className='adm-dot adm-dot--reader' />
                <span className='adm-prodrow__main'>
                  <span className='adm-prodrow__name'>RegulationCity App</span>
                  <span className='adm-prodrow__sub'>
                    {reader.hasSubscription ? reader.planName : 'No subscription'}
                  </span>
                </span>
                <span className='adm-prodrow__right'>
                  {reader.hasSubscription ? (
                    <span className={`adm-status ${accessTone(true, reader.accessActive)}`}>
                      {reader.accessActive ? reader.status : 'No access'}
                    </span>
                  ) : (
                    <span className='adm-table__muted'>—</span>
                  )}
                </span>
              </div>
              <div className='adm-prodrow'>
                <span className='adm-dot adm-dot--api' />
                <span className='adm-prodrow__main'>
                  <span className='adm-prodrow__name'>Developer API</span>
                  <span className='adm-prodrow__sub'>
                    {api.hasApiSubscription ? apiPlanLabel : 'No subscription'}
                  </span>
                </span>
                <span className='adm-prodrow__right'>
                  {api.hasApiSubscription ? (
                    <>
                      <span className='adm-status adm-status--handled'>{api.status}</span>
                      <span className='adm-prodrow__usage'>
                        {fmt(api.used)}
                        {api.callLimit > 0 ? ` / ${fmt(api.callLimit)}` : ' · uncapped'}
                      </span>
                    </>
                  ) : (
                    <span className='adm-table__muted'>—</span>
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className='adm-panel adm-panel--flush'>
            <div className='adm-panel__head'>
              <b>Commercial</b>
            </div>
            <div className='adm-panel__body'>
              {contractValue > 0 ? (
                <div className='adm-orgvalue'>
                  ${contractValue.toLocaleString()}
                  <span className='adm-orgvalue__unit'> / year combined</span>
                </div>
              ) : (
                <div className='adm-table__muted'>No negotiated price recorded.</div>
              )}
              <div className='adm-kv'>
                {reader.priceOverride != null && (
                  <div className='adm-kv__row'>
                    <span className='adm-kv__k'>App contract</span>
                    <span className='adm-kv__v'>
                      ${Number(reader.priceOverride).toLocaleString()}
                      {intervalSuffix(reader.priceInterval)}
                    </span>
                  </div>
                )}
                {api.priceOverride != null && (
                  <div className='adm-kv__row'>
                    <span className='adm-kv__k'>API contract</span>
                    <span className='adm-kv__v'>
                      ${Number(api.priceOverride).toLocaleString()}
                      {intervalSuffix(api.priceInterval)}
                    </span>
                  </div>
                )}
                {(reader.purchaseOrder || api.purchaseOrder) && (
                  <div className='adm-kv__row'>
                    <span className='adm-kv__k'>Purchase order</span>
                    <span className='adm-kv__v'>{reader.purchaseOrder || api.purchaseOrder}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className='adm-panel adm-panel--flush'>
            <div className='adm-panel__head'>
              <b>Renewal</b>
            </div>
            <div className='adm-panel__body'>
              {nextRenewal ? (
                <div className='adm-kv'>
                  <div className='adm-kv__row'>
                    <span className='adm-kv__k'>{nextRenewal.trial ? 'Trial ends' : 'Next date'}</span>
                    <span className='adm-kv__v'>{shortDate(nextRenewal.date)}</span>
                  </div>
                  <div className='adm-kv__row'>
                    <span className='adm-kv__k'>Countdown</span>
                    <span className='adm-kv__v'>
                      {renewalDays < 0 ? `${Math.abs(renewalDays)} days overdue` : `in ${renewalDays} days`}
                    </span>
                  </div>
                </div>
              ) : (
                <div className='adm-table__muted'>No contract dates recorded.</div>
              )}
              <div className='adm-cellhint'>
                Contract dates are recorded for reference. Access continues while the subscription is
                Active — an end date on its own does not cut anyone off.
              </div>
            </div>
          </div>

          <div className='adm-panel adm-panel--flush'>
            <div className='adm-panel__head'>
              <b>Organisation</b>
            </div>
            <div className='adm-panel__body'>
              <div className='adm-kv'>
                <div className='adm-kv__row'>
                  <span className='adm-kv__k'>Seats used</span>
                  <span className='adm-kv__v'>
                    {detail.seatCap ? `${detail.seatsUsed} of ${detail.seatCap}` : `${detail.seatsUsed} (no cap set)`}
                  </span>
                </div>
                <div className='adm-kv__row'>
                  <span className='adm-kv__k'>Active API keys</span>
                  <span className='adm-kv__v'>{fmt(api.activeKeyCount)}</span>
                </div>
              </div>

              <div className='adm-field__label' style={{ marginTop: '.9rem' }}>
                Organisation email domains
              </div>
              <input
                className='adm-input'
                value={allowedDomains}
                onChange={(e) => setAllowedDomains(e.target.value)}
                placeholder='e.g. acme.com, contoso.com'
                style={{ width: '100%' }}
              />
              <div className='adm-cellhint'>
                Domains this organisation owns (comma separated). Saved with the next Enterprise
                provision on either product — the basis for SSO and domain-based invites later.
              </div>
              {domainList.length > 0 && (
                <div className='adm-chips'>
                  {domainList.map((d) => (
                    <span key={d} className='adm-chip'>
                      {d}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {(reader.notes || api.notes) && (
            <div className='adm-panel adm-panel--flush'>
              <div className='adm-panel__head'>
                <b>Internal notes</b>
              </div>
              <div className='adm-panel__body'>
                {reader.notes && (
                  <p className='adm-orgnote'>
                    <span className='adm-dot adm-dot--reader' /> {reader.notes}
                  </p>
                )}
                {api.notes && (
                  <p className='adm-orgnote'>
                    <span className='adm-dot adm-dot--api' /> {api.notes}
                  </p>
                )}
                <div className='adm-field__label'>Not visible to the customer</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── App subscription ── */}
      {tab === 'app' && (
        <div className='adm-panel adm-panel--flush'>
          <div className='adm-panel__head'>
            <span className='adm-dot adm-dot--reader' />
            <b>RegulationCity App</b>
          </div>
          <div className='adm-panel__body'>
            <div style={{ marginBottom: '.85rem' }}>
              {reader.hasSubscription ? (
                <>
                  <span className={`adm-badge ${reader.accessActive ? 'adm-badge--ok' : ''}`}>
                    {reader.planName}
                    {reader.status ? ` · ${reader.status}` : ''}
                  </span>
                  {reader.trialEndsAt && (
                    <span className='adm-status adm-status--handled' style={{ marginLeft: '.5rem' }}>
                      {reader.accessActive
                        ? `Trial ends ${new Date(reader.trialEndsAt).toLocaleDateString()}`
                        : `Trial expired ${new Date(reader.trialEndsAt).toLocaleDateString()}`}
                    </span>
                  )}
                  {reader.priceOverride != null && (
                    <span className='adm-status' style={{ marginLeft: '.5rem' }}>
                      {priceTag(reader.priceOverride, reader.priceInterval)}
                    </span>
                  )}
                  {reader.seats != null && (
                    <span className='adm-status' style={{ marginLeft: '.5rem' }}>
                      {reader.seats} seats
                    </span>
                  )}
                </>
              ) : (
                <span className='adm-table__muted'>No RegulationCity App subscription.</span>
              )}
            </div>

            <div className='adm-inline adm-inline--fields'>
              <select
                className='adm-input'
                style={{ width: 'auto', minWidth: '150px' }}
                value={readerPlanSel}
                onChange={(e) => setReaderPlanSel(e.target.value)}
              >
                <option value=''>Select a plan…</option>
                {readerPlans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <button
                className='adm-btn adm-btn--teal'
                type='button'
                onClick={applyReaderPlan}
                disabled={readerBusy || !readerPlanSel}
              >
                {reader.hasSubscription ? 'Change plan' : 'Comp plan'}
              </button>
              {reader.hasSubscription && (
                <button
                  className='adm-btn adm-btn--ghost'
                  type='button'
                  onClick={handleCancelReader}
                  disabled={readerBusy}
                >
                  Cancel
                </button>
              )}
            </div>

            {isReaderTrial && (
              <div className='adm-subpanel' style={{ marginTop: '.85rem' }}>
                <div className='adm-field__label'>Extend the trial</div>
                <div className='adm-inline adm-inline--fields'>
                  <input
                    className='adm-input'
                    type='number'
                    min='1'
                    max='365'
                    value={extendDays}
                    onChange={(e) => setExtendDays(e.target.value)}
                    style={{ width: '90px' }}
                    aria-label='Days to extend the trial by'
                  />
                  <span className='adm-planfield__unit'>days</span>
                  <button
                    className='adm-btn adm-btn--teal'
                    type='button'
                    onClick={handleExtendReaderTrial}
                    disabled={readerBusy}
                  >
                    {readerBusy ? 'Working…' : 'Extend'}
                  </button>
                </div>
                <div className='adm-cellhint'>
                  {reader.accessActive
                    ? 'Added to the time still left on the trial.'
                    : 'This trial has run out, so the days are counted from today and access resumes.'}{' '}
                  Their allowances start a fresh cycle, as they do on a re-invite.
                </div>
              </div>
            )}

            <div className='adm-subpanel' style={{ marginTop: '.85rem' }}>
              <div className='adm-field__label'>Provision Enterprise (sales-led)</div>
              <div className='adm-inline adm-inline--fields'>
                <span className='adm-inputgroup'>
                  <span className='adm-inputgroup__addon'>$</span>
                  <input
                    className='adm-input adm-input--joined'
                    type='number'
                    min='0'
                    step='0.01'
                    value={readerPrice}
                    onChange={(e) => setReaderPrice(e.target.value)}
                    placeholder='Negotiated price'
                  />
                </span>
                <select
                  className='adm-input'
                  style={{ width: 'auto', minWidth: '110px' }}
                  value={readerInterval}
                  onChange={(e) => setReaderInterval(e.target.value)}
                  aria-label='Billing period'
                >
                  <option value='Monthly'>per month</option>
                  <option value='Annual'>per year</option>
                </select>
                <button
                  className='adm-btn adm-btn--primary'
                  type='button'
                  onClick={handleProvisionReaderEnterprise}
                  disabled={readerBusy}
                >
                  {isReaderEnterprise ? 'Update Enterprise' : 'Provision Enterprise'}
                </button>
              </div>
              <div className='adm-inline adm-inline--fields' style={{ marginTop: '.5rem' }}>
                <input
                  className='adm-input'
                  type='number'
                  min='1'
                  value={readerSeats}
                  onChange={(e) => setReaderSeats(e.target.value)}
                  placeholder='Seats'
                  style={{ width: '90px' }}
                />
                <input
                  className='adm-input'
                  type='email'
                  value={readerAdminEmail}
                  onChange={(e) => setReaderAdminEmail(e.target.value)}
                  placeholder='Admin email'
                  style={{ flex: 1, minWidth: '200px' }}
                />
              </div>
              <div className='adm-inline adm-inline--fields' style={{ marginTop: '.4rem' }}>
                <input
                  className='adm-input'
                  value={readerAdminFirst}
                  onChange={(e) => setReaderAdminFirst(e.target.value)}
                  placeholder='Admin first name (new admin only)'
                  style={{ flex: 1, minWidth: '160px' }}
                />
                <input
                  className='adm-input'
                  value={readerAdminLast}
                  onChange={(e) => setReaderAdminLast(e.target.value)}
                  placeholder='Admin last name'
                  style={{ flex: 1, minWidth: '140px' }}
                />
              </div>
              <div
                className='adm-inline adm-inline--fields'
                style={{ marginTop: '.4rem', alignItems: 'center' }}
              >
                <span className='adm-cellhint' style={{ margin: 0 }}>
                  Contract
                </span>
                <input
                  className='adm-input'
                  type='date'
                  aria-label='Contract start'
                  value={readerStart}
                  onChange={(e) => setReaderStart(e.target.value)}
                  style={{ width: '150px' }}
                />
                <span className='adm-cellhint' style={{ margin: 0 }}>
                  →
                </span>
                <input
                  className='adm-input'
                  type='date'
                  aria-label='Contract end'
                  value={readerEnd}
                  onChange={(e) => setReaderEnd(e.target.value)}
                  style={{ width: '150px' }}
                />
              </div>
              <input
                className='adm-input'
                value={readerPO}
                onChange={(e) => setReaderPO(e.target.value)}
                placeholder='PO / contract reference'
                style={{ marginTop: '.4rem', width: '100%' }}
              />
              <textarea
                className='adm-input'
                value={readerNotes}
                onChange={(e) => setReaderNotes(e.target.value)}
                placeholder='Internal notes (not shown to the customer)'
                rows={2}
                style={{ marginTop: '.4rem', width: '100%', resize: 'vertical' }}
              />
              <div className='adm-cellhint'>
                Moves this account onto the Enterprise plan (perpetual) and records the negotiated
                price + period, seat count, contract dates, PO and notes (for our records — no
                automatic billing; dates are reference-only, access stays active regardless of the end
                date). The admin (defaults to the account holder) becomes the org's CorporateAdmin and
                can invite their team up to the seat count.
              </div>
            </div>

            {reader.provider && (
              <div className='adm-note' style={{ fontSize: '.82rem', marginTop: '.6rem' }}>
                Billed via {reader.provider} — changes here override the local record only, not the
                provider.
              </div>
            )}
            {readerMsg && (
              <div
                className='adm-note'
                style={{
                  marginTop: '.5rem',
                  color: readerMsg.startsWith('✓') ? '#0f7d68' : '#dc3545',
                  fontWeight: 500,
                }}
              >
                {readerMsg}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── API subscription ── */}
      {tab === 'api' && (
        <div className='adm-panel adm-panel--flush'>
          <div className='adm-panel__head'>
            <span className='adm-dot adm-dot--api' />
            <b>Developer API</b>
          </div>
          <div className='adm-panel__body'>
            <div style={{ marginBottom: '.85rem' }}>
              {api.hasApiSubscription ? (
                <>
                  <span className={`adm-badge ${isProduction ? 'adm-badge--ok' : ''}`}>
                    {apiPlanLabel}
                    {api.status ? ` · ${api.status}` : ''}
                  </span>
                  {api.priceOverride != null && (
                    <span className='adm-status' style={{ marginLeft: '.5rem' }}>
                      {priceTag(api.priceOverride, api.priceInterval)}
                    </span>
                  )}
                  {api.seats != null && (
                    <span className='adm-status' style={{ marginLeft: '.5rem' }}>
                      {api.seats} seats
                    </span>
                  )}
                </>
              ) : (
                <span className='adm-table__muted'>No API subscription.</span>
              )}
            </div>

            {api.hasApiSubscription && (
              <div className='adm-inline adm-inline--fields' style={{ marginBottom: '.85rem' }}>
                {isProduction && (
                  <button
                    className='adm-btn adm-btn--ghost'
                    type='button'
                    onClick={handleDowngradeApi}
                    disabled={busy}
                  >
                    Downgrade to Trial
                  </button>
                )}
                <button
                  className='adm-btn adm-btn--ghost'
                  type='button'
                  onClick={handleCancelApi}
                  disabled={busy}
                >
                  Cancel API
                </button>
              </div>
            )}

            <div className='adm-subpanel'>
              <div className='adm-field__label'>Provision Enterprise (sales-led)</div>
              <div className='adm-inline adm-inline--fields'>
                <span className='adm-inputgroup'>
                  <span className='adm-inputgroup__addon'>$</span>
                  <input
                    className='adm-input adm-input--joined'
                    type='number'
                    min='0'
                    step='0.01'
                    value={apiPrice}
                    onChange={(e) => setApiPrice(e.target.value)}
                    placeholder='Negotiated price'
                  />
                </span>
                <select
                  className='adm-input'
                  style={{ width: 'auto', minWidth: '110px' }}
                  value={apiInterval}
                  onChange={(e) => setApiInterval(e.target.value)}
                  aria-label='Billing period'
                >
                  <option value='Monthly'>per month</option>
                  <option value='Annual'>per year</option>
                </select>
                <button
                  className='adm-btn adm-btn--primary'
                  type='button'
                  onClick={handleProvision}
                  disabled={busy}
                >
                  {busy ? 'Working…' : isProduction ? 'Update Enterprise' : 'Provision Enterprise'}
                </button>
              </div>
              <div className='adm-inline adm-inline--fields' style={{ marginTop: '.5rem' }}>
                <input
                  className='adm-input'
                  type='number'
                  min='1'
                  value={apiSeats}
                  onChange={(e) => setApiSeats(e.target.value)}
                  placeholder='Seats'
                  style={{ width: '90px' }}
                />
                <input
                  className='adm-input'
                  type='email'
                  value={apiAdminEmail}
                  onChange={(e) => setApiAdminEmail(e.target.value)}
                  placeholder='Admin email'
                  style={{ flex: 1, minWidth: '200px' }}
                />
              </div>
              <div className='adm-inline adm-inline--fields' style={{ marginTop: '.4rem' }}>
                <input
                  className='adm-input'
                  value={apiAdminFirst}
                  onChange={(e) => setApiAdminFirst(e.target.value)}
                  placeholder='Admin first name (new admin only)'
                  style={{ flex: 1, minWidth: '160px' }}
                />
                <input
                  className='adm-input'
                  value={apiAdminLast}
                  onChange={(e) => setApiAdminLast(e.target.value)}
                  placeholder='Admin last name'
                  style={{ flex: 1, minWidth: '140px' }}
                />
              </div>
              <div
                className='adm-inline adm-inline--fields'
                style={{ marginTop: '.4rem', alignItems: 'center' }}
              >
                <span className='adm-cellhint' style={{ margin: 0 }}>
                  Contract
                </span>
                <input
                  className='adm-input'
                  type='date'
                  aria-label='Contract start'
                  value={apiStart}
                  onChange={(e) => setApiStart(e.target.value)}
                  style={{ width: '150px' }}
                />
                <span className='adm-cellhint' style={{ margin: 0 }}>
                  →
                </span>
                <input
                  className='adm-input'
                  type='date'
                  aria-label='Contract end'
                  value={apiEnd}
                  onChange={(e) => setApiEnd(e.target.value)}
                  style={{ width: '150px' }}
                />
              </div>
              <input
                className='adm-input'
                value={apiPO}
                onChange={(e) => setApiPO(e.target.value)}
                placeholder='PO / contract reference'
                style={{ marginTop: '.4rem', width: '100%' }}
              />
              <textarea
                className='adm-input'
                value={apiNotes}
                onChange={(e) => setApiNotes(e.target.value)}
                placeholder='Internal notes (not shown to the customer)'
                rows={2}
                style={{ marginTop: '.4rem', width: '100%', resize: 'vertical' }}
              />
              <div className='adm-cellhint'>
                Moves this account onto the Enterprise API plan (perpetual; unlocks API keys and the
                higher call limit) and records the negotiated price + period, seat count, contract
                dates, PO and notes (for our records — no automatic billing; dates are reference-only,
                access stays active regardless of the end date). The admin (defaults to the account
                holder) becomes the org's CorporateAdmin and can invite their team up to the seat
                count.
              </div>
            </div>

            {api.hasApiSubscription && (
              <div className='adm-subpanel' style={{ marginTop: '.85rem' }}>
                <div className='adm-field__label'>
                  {isApiTrial ? 'Extend the trial — call allowance' : 'Call limit'}
                </div>
                <div className='adm-inline adm-inline--fields'>
                  <input
                    className='adm-input'
                    type='number'
                    min='1'
                    value={limitInput}
                    onChange={(e) => setLimitInput(e.target.value)}
                    style={{ width: '110px' }}
                    placeholder='Calls'
                  />
                  <select
                    className='adm-input'
                    style={{ width: 'auto', minWidth: '110px' }}
                    value={quotaInterval}
                    onChange={(e) => setQuotaInterval(e.target.value)}
                    aria-label='Call-limit period'
                  >
                    <option value='Total'>in total (one-off)</option>
                    <option value='Monthly'>per month</option>
                    <option value='Annual'>per year</option>
                  </select>
                  <button
                    className='adm-btn adm-btn--teal'
                    type='button'
                    onClick={handleSaveLimit}
                    disabled={busy}
                  >
                    Save
                  </button>
                  {api.quotaOverride != null && (
                    <button
                      className='adm-btn adm-btn--ghost'
                      type='button'
                      onClick={handleResetLimit}
                      disabled={busy}
                    >
                      Reset
                    </button>
                  )}
                </div>
                <div className='adm-cellhint'>
                  <b>In total</b> is a one-off allowance that never resets — the shape a trial has, so
                  it is how you give a trial customer more calls without turning their trial into a
                  recurring quota. <b>Per month</b> and <b>per year</b> refill every calendar window,
                  which is the Enterprise shape. Reset reverts to the plan default.
                </div>
              </div>
            )}

            {msg && (
              <div
                className='adm-note'
                style={{
                  marginTop: '.5rem',
                  color: msg.startsWith('✓') ? '#0f7d68' : '#dc3545',
                  fontWeight: 500,
                }}
              >
                {msg}
              </div>
            )}

            {api.hasApiSubscription && (
              <div className='adm-stats' style={{ marginTop: '1.5rem' }}>
                <div className='adm-stat'>
                  <div className='adm-stat__label'>API usage ({quotaPeriodLong})</div>
                  <div className='adm-stat__value'>
                    {fmt(api.used)}
                    {api.callLimit > 0 ? ` / ${fmt(api.callLimit)}` : ' (uncapped)'}
                  </div>
                  {api.callLimit > 0 && (
                    <div className='adm-bar'>
                      <div
                        className={`adm-bar__fill ${usagePct >= 100 ? 'adm-bar__fill--full' : ''}`}
                        style={{ width: `${usagePct}%` }}
                      />
                    </div>
                  )}
                </div>
                <div className='adm-stat'>
                  <div className='adm-stat__label'>Call limit</div>
                  <div className='adm-stat__value'>
                    {api.callLimit > 0 ? `${fmt(api.callLimit)}${quotaPeriodShort}` : 'Uncapped'}
                    {api.quotaOverride != null && (
                      <span className='adm-badge adm-badge--tag'>custom</span>
                    )}
                  </div>
                </div>
                <div className='adm-stat'>
                  <div className='adm-stat__label'>Active API keys</div>
                  <div className='adm-stat__value'>{fmt(api.activeKeyCount)}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Team ── */}
      {tab === 'team' && (
        <div className='adm-panel adm-panel--flush'>
          <div className='adm-panel__head'>
            <b>Team members</b>
            <span className='adm-panel__headnote'>
              {detail.seatCap
                ? `${detail.seatsUsed} of ${detail.seatCap} seats used`
                : `${detail.seatsUsed} logins · no seat cap set`}
            </span>
          </div>
          <div className='adm-panel__body'>
            {members === null ? (
              <Loader />
            ) : members.error ? (
              <div className='adm-alert adm-alert--err'>{members.error}</div>
            ) : members.length === 0 ? (
              <div className='adm-empty'>No logins on this organisation yet.</div>
            ) : (
              <>
                <div className='adm-tablewrap'>
                  <table className='adm-table'>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((m) => (
                        <tr key={m.id}>
                          <td className='adm-table__nowrap'>{m.name || '—'}</td>
                          <td className='adm-table__nowrap'>{m.email}</td>
                          <td className='adm-table__nowrap'>
                            <span
                              className={`adm-status ${
                                m.isAdmin ? 'adm-status--handled' : 'adm-status--archived'
                              }`}
                            >
                              {m.isAdmin ? 'Organisation admin' : 'Member'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className='adm-cellhint'>
                  {detail.seatCap && detail.seatCap > detail.seatsUsed
                    ? `${detail.seatCap - detail.seatsUsed} seats unused. `
                    : ''}
                  The organisation admin invites their own members from the customer portal; the seat
                  cap comes from the Enterprise deal.
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Activity ── */}
      {tab === 'activity' && (
        <div className='adm-panel'>
          <div className='adm-field__label'>Activity</div>
          {audit && audit.length ? (
            <div className='adm-audit'>
              {audit.map((a, i) => (
                <div key={i} className='adm-audit__row'>
                  <span className='adm-audit__when'>
                    {a.createdAt ? new Date(a.createdAt).toLocaleString() : ''}
                  </span>
                  <span className='adm-audit__detail'>
                    {a.detail || a.action}
                    {a.productLine ? ` · ${a.productLine === 'DeveloperApi' ? 'API' : 'App'}` : ''}
                  </span>
                  <span className='adm-audit__who'>{a.performedBy || '—'}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className='adm-table__muted'>No activity recorded yet.</div>
          )}
        </div>
      )}
    </section>
  );
};

export default connector(CustomerDetail);
